// Sprint 80 — Admin System Panel · /dashboard/admin/system
//
// Three tabs for ops visibility:
//   1. Couriers     — registry matrix · retry-booking · manual-AWB · label
//   2. WhatsApp     — status · template list · log viewer · test-send
//   3. Rate Limits  — buckets currently approaching limits
//
// All backend endpoints already exist; this file is the React UI layer.

import { useEffect, useState } from "react";
import { Loader2, RefreshCw, Send, Search, AlertCircle, ShieldCheck, MessageCircle, Truck, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import RoleGate from "../../components/RoleGate";

const TABS = [
  { id: "couriers",    label: "Couriers",       icon: Truck },
  { id: "whatsapp",    label: "WhatsApp",       icon: MessageCircle },
  { id: "ratelimits",  label: "Rate Limits",    icon: ShieldCheck },
  { id: "gst",         label: "GSTR-1 Export",  icon: FileText },
];


// ──────────────────────────── Tab 1: Couriers ────────────────────────────
function CourierTab() {
  const [registry, setRegistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackingId, setTrackingId] = useState("");
  const [orderDetail, setOrderDetail] = useState(null);
  const [manualCourier, setManualCourier] = useState("");
  const [manualAwb, setManualAwb] = useState("");

  const refresh = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/courier/registry");
      setRegistry(data.matrix || {});
    } catch (e) {
      toast.error("Failed to load courier registry");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/courier/registry");
        if (!cancelled) setRegistry(data.matrix || {});
      } catch { /* toast handled on manual refresh */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const lookup = async () => {
    if (!trackingId.trim()) return;
    try {
      const { data } = await api.get(`/admin/courier/${trackingId.trim()}/detail`);
      setOrderDetail(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Order not found");
      setOrderDetail(null);
    }
  };

  const retry = async () => {
    try {
      const { data } = await api.post(`/admin/courier/${trackingId.trim()}/retry-booking`);
      toast.success(`Retry → ${data.result?.ok ? "booked" : "failed"}`);
      lookup();
    } catch (e) { toast.error("Retry failed"); }
  };

  const submitManualAwb = async () => {
    if (!manualCourier || !manualAwb) { toast.error("Courier + AWB required"); return; }
    try {
      await api.post(`/admin/courier/${trackingId.trim()}/manual-awb`,
                     { courier_partner: manualCourier, awb: manualAwb });
      toast.success("Manual AWB saved");
      setManualCourier(""); setManualAwb("");
      lookup();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="flex items-center gap-2 text-slate-500 py-8"><Loader2 className="animate-spin h-4 w-4" /> Loading…</div>;
  if (!registry) return null;

  return (
    <div className="space-y-6" data-testid="admin-courier-tab">
      {/* Registry matrix */}
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-slate-50">
          <h3 className="font-semibold text-sm">Courier Registry · {Object.keys(registry).length} providers</h3>
          <button onClick={refresh} className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1" data-testid="courier-registry-refresh">
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50/60 text-slate-600 text-xs">
            <tr><th className="px-4 py-2 text-left">Courier</th><th className="text-left">Mode</th><th className="text-left">Creds</th><th className="text-left">Active class</th></tr>
          </thead>
          <tbody>
            {Object.entries(registry).map(([id, info]) => (
              <tr key={id} className="border-t border-slate-100" data-testid={`courier-row-${id}`}>
                <td className="px-4 py-2 font-medium">{id}</td>
                <td>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${info.mode === "live" ? "bg-green-100 text-green-700" : info.mode === "sandbox" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-600"}`}>
                    {info.mode}
                  </span>
                </td>
                <td>{info.creds_set ? <span className="text-green-600 text-xs font-bold">✓ set</span> : <span className="text-slate-400 text-xs">missing</span>}</td>
                <td className="text-slate-500 text-xs font-mono">{info.active_class}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Order lookup */}
      <div className="border border-slate-200 rounded-xl p-4">
        <h3 className="font-semibold text-sm mb-3">Order courier detail · retry · manual AWB</h3>
        <div className="flex gap-2 mb-3">
          <input className="flex-1 border border-slate-200 rounded px-3 py-2 text-sm" placeholder="PKG-XXXXXXXX" value={trackingId} onChange={e => setTrackingId(e.target.value)} data-testid="courier-lookup-input" />
          <button onClick={lookup} className="px-4 py-2 bg-[#FF6B00] text-white rounded text-sm flex items-center gap-1" data-testid="courier-lookup-btn"><Search className="h-3 w-3" /> Look up</button>
        </div>
        {orderDetail && (
          <div className="mt-3 space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded">
              <div><span className="text-slate-500 text-xs">Status:</span> <span className="font-medium">{orderDetail.courier_status || "—"}</span></div>
              <div><span className="text-slate-500 text-xs">Partner:</span> <span className="font-mono">{orderDetail.courier_partner || "—"}</span></div>
              <div><span className="text-slate-500 text-xs">AWB:</span> <span className="font-mono">{orderDetail.courier_awb || "—"}</span></div>
              <div><span className="text-slate-500 text-xs">Mock:</span> <span>{orderDetail.courier_is_mock ? "yes" : "no"}</span></div>
            </div>
            {orderDetail.courier_last_error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-xs flex gap-2"><AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" /> {orderDetail.courier_last_error}</div>
            )}
            <div className="flex flex-wrap gap-2">
              <button onClick={retry} className="px-3 py-1.5 bg-slate-900 text-white rounded text-xs" data-testid="courier-retry-btn">Retry booking</button>
              {orderDetail.courier_awb && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/courier/${trackingId.trim()}/label`} target="_blank" rel="noreferrer" className="px-3 py-1.5 border border-slate-300 rounded text-xs">Download label</a>
              )}
            </div>
            <div className="border-t border-slate-200 pt-3">
              <p className="text-xs text-slate-500 mb-2">Manual AWB entry (when courier was booked outside PickNGo)</p>
              <div className="flex gap-2">
                <input className="border border-slate-200 rounded px-2 py-1.5 text-xs flex-1" placeholder="courier_id (e.g. delhivery)" value={manualCourier} onChange={e=>setManualCourier(e.target.value)} data-testid="manual-courier-input" />
                <input className="border border-slate-200 rounded px-2 py-1.5 text-xs flex-1" placeholder="AWB / LRN" value={manualAwb} onChange={e=>setManualAwb(e.target.value)} data-testid="manual-awb-input" />
                <button onClick={submitManualAwb} className="px-3 py-1.5 bg-[#FF6B00] text-white rounded text-xs" data-testid="manual-awb-submit">Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ──────────────────────────── Tab 2: WhatsApp ────────────────────────────
function WhatsAppTab() {
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [logTid, setLogTid] = useState("");
  const [logs, setLogs] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [testTpl, setTestTpl] = useState("order_confirmed");

  const fetchLogs = async (tid = "") => {
    try {
      const url = tid ? `/admin/whatsapp/log?tracking_id=${encodeURIComponent(tid)}&limit=20` : "/admin/whatsapp/log?limit=20";
      const { data } = await api.get(url);
      setLogs(data.rows || []);
    } catch { setLogs([]); }
  };

  const doTestSend = async (phone, tplKey) => {
    let ok = false;
    try {
      const { data } = await api.post("/admin/whatsapp/test", { to: phone, template_key: tplKey });
      toast.success(data.mock ? `Mock sent → ${data.provider_message_id}` : `LIVE sent → ${data.provider_message_id}`);
      ok = true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    if (ok) fetchLogs();
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, t, l] = await Promise.all([
          api.get("/admin/whatsapp/status"),
          api.get("/admin/whatsapp/templates"),
          api.get("/admin/whatsapp/log?limit=20"),
        ]);
        if (cancelled) return;
        setStatus(s.data);
        setTemplates(t.data.templates || []);
        setLogs(l.data?.rows || []);
      } catch {
        if (!cancelled) setLogs([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-whatsapp-tab">
      {/* Status */}
      {status && (
        <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
          <div className="flex flex-wrap gap-4 text-sm">
            <div><span className="text-xs text-slate-500">Mode</span><br/><span className={`font-bold ${status.mode === "live" ? "text-green-600" : "text-slate-700"}`}>{status.mode}</span></div>
            <div><span className="text-xs text-slate-500">Provider</span><br/><span className="font-bold">{status.active_provider}</span></div>
            <div><span className="text-xs text-slate-500">MSG91 creds</span><br/><span>{status.providers?.msg91?.creds_set ? "✓ set" : "missing"}</span></div>
            <div><span className="text-xs text-slate-500">Meta creds</span><br/><span>{status.providers?.meta_cloud?.creds_set ? "✓ set" : "missing"}</span></div>
          </div>
        </div>
      )}

      {/* Test send */}
      <div className="border border-slate-200 rounded-xl p-4">
        <h3 className="font-semibold text-sm mb-3">Send test message</h3>
        <div className="flex flex-wrap gap-2">
          <input className="border border-slate-200 rounded px-3 py-2 text-sm w-44" placeholder="9876543210" value={testTo} onChange={e=>setTestTo(e.target.value)} data-testid="wa-test-to" />
          <select className="border border-slate-200 rounded px-3 py-2 text-sm" value={testTpl} onChange={e=>setTestTpl(e.target.value)} data-testid="wa-test-tpl">
            {templates.map(t => <option key={t.key} value={t.key}>{t.key}</option>)}
          </select>
          <button onClick={() => { if (testTo && testTpl) doTestSend(testTo, testTpl); }} className="px-3 py-2 bg-[#FF6B00] text-white rounded text-sm flex items-center gap-1" data-testid="wa-test-send"><Send className="h-3 w-3"/> Send</button>
        </div>
      </div>

      {/* Logs */}
      <div className="border border-slate-200 rounded-xl">
        <div className="flex items-center justify-between px-4 py-3 bg-slate-50">
          <h3 className="font-semibold text-sm">Recent sends</h3>
          <div className="flex gap-1">
            <input className="border border-slate-200 rounded px-2 py-1 text-xs w-44" placeholder="filter by tracking_id" value={logTid} onChange={e=>setLogTid(e.target.value)} data-testid="wa-log-filter" />
            <button onClick={() => fetchLogs(logTid)} className="px-2 py-1 border border-slate-300 rounded text-xs">Filter</button>
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50/60 text-slate-500"><tr><th className="px-3 py-2 text-left">Time</th><th className="text-left">Template</th><th className="text-left">To</th><th className="text-left">Status</th></tr></thead>
            <tbody>
              {logs.length === 0 && <tr><td colSpan="4" className="px-3 py-6 text-center text-slate-400">No messages yet</td></tr>}
              {logs.map((r, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`wa-log-row-${i}`}>
                  <td className="px-3 py-1.5 text-slate-500 font-mono">{(r.created_at || "").slice(11,19)}</td>
                  <td className="font-medium">{r.template_key}</td>
                  <td className="font-mono">{r.to}</td>
                  <td><span className={`px-1.5 py-0.5 rounded text-xs ${r.status === "sent" ? "bg-green-100 text-green-700" : r.status === "failed" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>{r.status}{r.mock && " (mock)"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ──────────────────────────── Tab 3: Rate Limits ────────────────────────────
function RateLimitsTab() {
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Backend route for live aggregation not yet implemented — placeholder.
      if (!cancelled) {
        setBuckets([]);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-4" data-testid="admin-ratelimits-tab">
      <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 text-sm">
        <p className="font-semibold text-amber-900 flex items-center gap-1"><AlertCircle className="h-4 w-4"/> Coming soon</p>
        <p className="text-amber-800 text-xs mt-1">
          Live rate-limit hits dashboard. The data already exists in <code>db.rate_limits</code> —
          read it in MongoDB Atlas → Collections → rate_limits for now. We will surface
          it here once usage volume justifies the dedicated read-path API.
        </p>
      </div>
      <div className="text-xs text-slate-500">
        Active limits (configured in <code>core/rate_limit.py</code>):
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li><b>POST /api/auth/login</b> — 10 attempts / 15 min per (ip, email)</li>
          <li><b>POST /api/auth/verify-otp</b> — 5 attempts / 10 min per (ip, email)</li>
          <li><b>POST /api/auth/resend-otp</b> — 3 / 10 min per email</li>
          <li><b>POST /api/auth/verify-registration</b> — 5 / 10 min per (ip, email)</li>
          <li><b>POST /api/auth/resend-registration-otp</b> — 3 / 10 min per email</li>
          <li><b>POST /api/auth/google</b> — 20 / hour per IP</li>
          <li><b>GET  /api/public/track/&#123;tid&#125;</b> — 120 / 1 min per IP</li>
        </ul>
      </div>
    </div>
  );
}


// ──────────────────────────── Tab 4: GSTR-1 Export ────────────────────────────
function GstTab() {
  const today = new Date();
  const _cyMonth = today.getUTCMonth() + 1; // 1-12
  const _cyYear = today.getUTCFullYear();
  const _fyStart = _cyMonth >= 4 ? _cyYear : _cyYear - 1;
  const _defaultFy = `${_fyStart}-${String((_fyStart + 1) % 100).padStart(2, "0")}`;
  const [fy, setFy] = useState(_defaultFy);
  const [month, setMonth] = useState(_cyMonth);
  const [section, setSection] = useState("all");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/gst/summary?fy=${fy}&month=${month}`);
      setSummary(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load summary");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get(`/admin/gst/export?fy=${fy}&month=${month}&section=${section}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `GSTR1-${fy}-M${String(month).padStart(2, "0")}-${section}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded — ready for GSTN portal upload");
    } catch (e) {
      toast.error("Export failed — check backend logs");
    }
  };

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const fyOptions = [];
  for (let i = _fyStart; i >= _fyStart - 3; i--) {
    fyOptions.push(`${i}-${String((i + 1) % 100).padStart(2, "0")}`);
  }

  return (
    <div className="space-y-5" data-testid="admin-system-gst">
      <div className="rounded-xl border border-slate-200 p-5 bg-white">
        <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <FileText className="h-4 w-4 text-[#FF6B00]"/> Generate GSTR-1 Export
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          Indian GST monthly return data. Filter by financial year and month — CSV is ready
          to upload directly to the GSTN portal or import into Tally / ClearTax / Zoho Books.
          SAC 996813 (Courier Services) is hard-coded.
        </p>
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div>
            <label className="text-xs text-slate-500 font-medium uppercase tracking-wider">Financial Year</label>
            <select value={fy} onChange={(e) => setFy(e.target.value)}
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="gst-fy-select">
              {fyOptions.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium uppercase tracking-wider">Month</label>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="gst-month-select">
              {months.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium uppercase tracking-wider">Section</label>
            <select value={section} onChange={(e) => setSection(e.target.value)}
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="gst-section-select">
              <option value="all">All (B2B + B2C)</option>
              <option value="b2b">B2B only (with GSTIN)</option>
              <option value="b2c">B2C only (no GSTIN)</option>
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button onClick={fetchSummary} disabled={loading}
              className="flex-1 px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm font-medium hover:bg-slate-50 disabled:opacity-60"
              data-testid="gst-summary-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin inline"/> : "Preview"}
            </button>
          </div>
        </div>
        <button onClick={downloadCsv}
          className="w-full px-4 py-3 rounded-lg bg-[#FF6B00] text-white font-semibold hover:bg-[#E55A00] flex items-center justify-center gap-2"
          data-testid="gst-download-csv">
          <Download className="h-4 w-4"/> Download GSTR-1 CSV
        </button>
      </div>

      {summary && (
        <div className="rounded-xl border border-slate-200 p-5 bg-slate-50" data-testid="gst-summary-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">Summary — {months[summary.month - 1]} {summary.fy}</h3>
            <span className="text-xs text-slate-500">Generated by {summary.generated_by}</span>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <SumCard label="Invoices" value={summary.invoice_count} />
            <SumCard label="B2B / B2C" value={`${summary.b2b_count} / ${summary.b2c_count}`} />
            <SumCard label="Taxable Value" value={`₹${summary.total_taxable_value.toLocaleString("en-IN")}`} />
            <SumCard label="Invoice Value" value={`₹${summary.total_invoice_value.toLocaleString("en-IN")}`} highlight />
            <SumCard label="CGST" value={`₹${summary.total_cgst.toLocaleString("en-IN")}`} />
            <SumCard label="SGST" value={`₹${summary.total_sgst.toLocaleString("en-IN")}`} />
            <SumCard label="IGST" value={`₹${summary.total_igst.toLocaleString("en-IN")}`} />
            <SumCard label="Total GST" value={`₹${summary.total_gst.toLocaleString("en-IN")}`} highlight />
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 p-4 text-xs text-slate-500 bg-white">
        <p className="font-medium text-slate-700 mb-1">CSV columns (GSTR-1 4A compatible)</p>
        <p>GSTIN/UIN · Receiver Name · Invoice Number · Invoice date · Invoice Value · Place Of Supply · Reverse Charge · Rate (18%) · Taxable Value · CGST · SGST · IGST · SAC (996813) · Tracking ID</p>
      </div>
    </div>
  );
}

function SumCard({ label, value, highlight }) {
  return (
    <div className={`rounded-lg p-3 border ${highlight ? "border-[#FF6B00] bg-orange-50" : "border-slate-200 bg-white"}`}>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
      <div className={`text-lg font-bold ${highlight ? "text-[#FF6B00]" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}


// ──────────────────────────── Main ────────────────────────────
export default function AdminSystem() {
  const [tab, setTab] = useState("couriers");
  return (
    <RoleGate allow={["admin", "founder", "ceo", "main_hub_manager", "ops_staff"]}>
      <div className="max-w-6xl mx-auto p-6" data-testid="admin-system-root">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">System Panel</h1>
          <p className="text-sm text-slate-500">Couriers · WhatsApp · Rate limits · Live integration status</p>
        </div>
        <div className="flex gap-1 border-b border-slate-200 mb-6">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 transition ${tab === t.id ? "border-[#FF6B00] text-[#FF6B00] font-semibold" : "border-transparent text-slate-500 hover:text-slate-900"}`}
                data-testid={`admin-system-tab-${t.id}`}
              >
                <Icon className="h-4 w-4"/> {t.label}
              </button>
            );
          })}
        </div>
        {tab === "couriers"   && <CourierTab />}
        {tab === "whatsapp"   && <WhatsAppTab />}
        {tab === "ratelimits" && <RateLimitsTab />}
        {tab === "gst"        && <GstTab />}
      </div>
    </RoleGate>
  );
}
